import { calendarCancellationReason } from "../services/calendarRequestDiagnostics";
import { DEFAULT_HTTP_TIMEOUT_MS } from "../services/httpTimeout";

// Availability lookups run inside a modal the customer is waiting on. If the
// provider stalls, an unbounded request leaves that modal on a spinner with no
// way forward, so every availability read is bounded and cleared on settle.
//
// The timeout is a hard race, not just an abort: a caller that ignores the
// abort signal (a plain promise, a mocked lookup) must still reject so the UI
// can leave its waiting state. The request is invoked synchronously so callers
// keep the same call ordering they had before the guard existed.
export const AVAILABILITY_LOOKUP_TIMEOUT_MS = 12000;
// Calendar alternatives may span staff and dates. Use the existing HTTP budget
// at both the transport and dialog boundary so a valid response is not discarded.
export const CALENDAR_AVAILABILITY_TIMEOUT_MS = DEFAULT_HTTP_TIMEOUT_MS;

export const withAvailabilityTimeout = (request, timeoutMs = AVAILABILITY_LOOKUP_TIMEOUT_MS, signal) =>
  new Promise((resolve, reject) => {
    const controller = new AbortController();
    let settled = false;
    let timer;
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal?.removeEventListener("abort", abort);
      callback(value);
    };
    const abort = () => {
      controller.abort(calendarCancellationReason(signal));
      const error = new Error("Availability lookup cancelled.");
      error.name = "AbortError";
      finish(reject, error);
    };
    if (signal?.aborted) { abort(); return; }
    signal?.addEventListener("abort", abort, { once: true });
    timer = setTimeout(() => {
      controller.abort("timeout");
      finish(reject, new Error("Availability lookup timed out."));
    }, timeoutMs);
    let pending;
    try {
      pending = request(controller.signal);
    } catch (error) {
      finish(reject, error);
      return;
    }
    Promise.resolve(pending).then(
      (value) => finish(resolve, value),
      (error) => finish(reject, error),
    );
  });
